import { Extension } from '@tiptap/core'
import { Plugin, PluginKey, TextSelection } from '@tiptap/pm/state'
import { Decoration, DecorationSet } from '@tiptap/pm/view'
import type { TiptapDocJSON } from '../utils/contentUtils'

/** Half-open [start, end) character range within a string. */
export type TextRange = [number, number]

export interface HighlightSegment {
  text: string
  match: boolean
  /** A match shown for context, not the one this snippet opens. */
  secondary?: boolean
}

export interface SearchSnippet {
  /** Zero-based index of the highlighted match within the whole document. */
  occurrence: number
  segments: HighlightSegment[]
}

export interface NodeSearchResult {
  titleRanges: TextRange[]
  /** Aliases that matched, with their match ranges. Empty when none did. */
  aliasMatches: Array<{ alias: string; ranges: TextRange[] }>
  bodyCount: number
  snippets: SearchSnippet[]
  score: number
}

export const MAX_SNIPPETS_PER_DOCUMENT = 50
const SNIPPET_RADIUS = 56

export function findMatchRanges(text: string, matcher: RegExp, limit = Infinity): TextRange[] {
  const ranges: TextRange[] = []
  matcher.lastIndex = 0
  let match: RegExpExecArray | null
  while (ranges.length < limit && (match = matcher.exec(text))) {
    if (match[0].length === 0) {
      matcher.lastIndex++
      continue
    }
    ranges.push([match.index, match.index + match[0].length])
  }
  return ranges
}

export function toHighlightSegments(
  text: string,
  ranges: TextRange[],
  primaryStart?: number,
): HighlightSegment[] {
  const segments: HighlightSegment[] = []
  let cursor = 0
  for (const [start, end] of ranges) {
    if (start > cursor) segments.push({ text: text.slice(cursor, start), match: false })
    segments.push({
      text: text.slice(start, end),
      match: true,
      ...(primaryStart !== undefined && start !== primaryStart ? { secondary: true } : {}),
    })
    cursor = end
  }
  if (cursor < text.length) segments.push({ text: text.slice(cursor), match: false })
  return segments
}

/**
 * Extracts one string per text block, in document order. Inline leaf nodes
 * (hard breaks, inline images) become a single space so string offsets map
 * one-to-one onto ProseMirror positions inside the block; see
 * `readEditorTextBlocks`, which must produce identical strings.
 */
export function extractTextBlocks(document: TiptapDocJSON | null | undefined): string[] {
  const blocks: string[] = []
  function visit(node: any) {
    const content: any[] = Array.isArray(node?.content) ? node.content : []
    if (content.some(child => typeof child?.text === 'string')) {
      blocks.push(content.map(child => typeof child?.text === 'string' ? child.text : ' ').join(''))
      return
    }
    content.forEach(visit)
  }
  visit(document)
  return blocks
}

export function readEditorTextBlocks(doc: any): Array<{ text: string; start: number }> {
  const blocks: Array<{ text: string; start: number }> = []
  doc.descendants((node: any, pos: number) => {
    if (!node.isTextblock) return true
    let text = ''
    let hasText = false
    node.forEach((child: any) => {
      if (child.isText) {
        text += child.text
        hasText = true
      } else {
        text += ' '
      }
    })
    if (hasText) blocks.push({ text, start: pos + 1 })
    return false
  })
  return blocks
}

function makeSnippet(block: string, range: TextRange, matcher: RegExp, occurrence: number): SearchSnippet {
  let from = Math.max(0, range[0] - SNIPPET_RADIUS)
  let to = Math.min(block.length, range[1] + SNIPPET_RADIUS)
  // Trim to word boundaries so an excerpt does not open or close mid-word.
  if (from > 0) {
    const space = block.indexOf(' ', from)
    if (space >= 0 && space < range[0]) from = space + 1
  }
  if (to < block.length) {
    const space = block.lastIndexOf(' ', to)
    if (space > range[1]) to = space
  }
  const raw = block.slice(from, to).replace(/\s/g, ' ')
  const excerpt = raw.trim()
  const primaryStart = range[0] - from - (raw.length - raw.trimStart().length)
  // Show every match in the excerpt, but mark which one this snippet opens.
  const segments = toHighlightSegments(excerpt, findMatchRanges(excerpt, matcher), primaryStart)
  if (from > 0) segments.unshift({ text: '…', match: false })
  if (to < block.length) segments.push({ text: '…', match: false })
  return { occurrence, segments }
}

export function searchTextBlocks(
  blocks: string[],
  matcher: RegExp,
  maximumSnippets = MAX_SNIPPETS_PER_DOCUMENT,
): { count: number; snippets: SearchSnippet[] } {
  let count = 0
  const snippets: SearchSnippet[] = []
  for (const block of blocks) {
    for (const range of findMatchRanges(block, matcher)) {
      if (snippets.length < maximumSnippets) snippets.push(makeSnippet(block, range, matcher, count))
      count++
    }
  }
  return { count, snippets }
}

// ── Revealing a match in the editor ───────────────────────────────

const searchFlashKey = new PluginKey<DecorationSet>('corpusSearchFlash')
const flashTimers = new WeakMap<object, ReturnType<typeof setTimeout>>()

/**
 * Installed with the editor, never registered later: adding a plugin to a
 * running editor recreates every plugin view, and y-prosemirror's undo view
 * destroys its UndoManager when that happens, silently ending Ctrl+Z for the
 * page.
 */
export const CorpusSearchFlash = Extension.create({
  name: 'corpusSearchFlash',
  addProseMirrorPlugins: () => [new Plugin<DecorationSet>({
    key: searchFlashKey,
    state: {
      init: () => DecorationSet.empty,
      apply(tr, previous) {
        const meta = tr.getMeta(searchFlashKey) as TextRange | null | undefined
        if (meta === null) return DecorationSet.empty
        if (meta) {
          return DecorationSet.create(tr.doc, [
            Decoration.inline(meta[0], meta[1], { class: 'corpus-search-flash' }),
          ])
        }
        return previous.map(tr.mapping, tr.doc)
      },
    },
    props: {
      decorations: state => searchFlashKey.getState(state) || DecorationSet.empty,
    },
  })],
})

/** Resolves once the editor has finished binding `pageId`, or after a timeout. */
export async function waitForEditorPage(
  editor: any,
  isActive: () => boolean,
  timeoutMs = 10000,
): Promise<boolean> {
  const started = Date.now()
  while (Date.now() - started < timeoutMs) {
    if (!isActive()) return false
    if (editor && !editor.isDestroyed && editor.isEditable) {
      // Let the collaboration binding render the hydrated document first.
      await new Promise(resolve => requestAnimationFrame(() => resolve(null)))
      return isActive()
    }
    await new Promise(resolve => setTimeout(resolve, 50))
  }
  return isActive()
}

/**
 * Selects the `occurrence`-th match of `matcher` in the open document, scrolls
 * it to the middle of the view, and briefly highlights it. Falls back to the
 * last match if the document has changed and has fewer matches than before.
 */
export function revealSearchMatch(
  editor: any,
  matcher: RegExp,
  occurrence: number,
  options: { focus?: boolean } = {},
): boolean {
  if (!editor?.view || editor.isDestroyed) return false

  let target: TextRange | null = null
  let index = 0
  for (const block of readEditorTextBlocks(editor.state.doc)) {
    for (const [start, end] of findMatchRanges(block.text, matcher)) {
      target = [block.start + start, block.start + end]
      if (index++ === occurrence) break
    }
    if (index > occurrence) break
  }
  if (!target) return false

  const { view } = editor
  const tr = view.state.tr
    .setSelection(TextSelection.create(view.state.doc, target[0], target[1]))
    .setMeta(searchFlashKey, target)
  view.dispatch(tr)
  if (options.focus) view.focus()

  try {
    const { node } = view.domAtPos(target[0])
    const element = node.nodeType === Node.ELEMENT_NODE ? node as Element : node.parentElement
    element?.scrollIntoView({ block: 'center', behavior: 'smooth' })
  } catch {
    view.dispatch(view.state.tr.scrollIntoView())
  }

  const previousTimer = flashTimers.get(editor)
  if (previousTimer) clearTimeout(previousTimer)
  flashTimers.set(editor, setTimeout(() => {
    if (!editor.isDestroyed) editor.view.dispatch(editor.view.state.tr.setMeta(searchFlashKey, null))
  }, 2400))
  return true
}
